import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppStoreUrls } from '@anihis/shared/src/constants/app-store-ids';
import { environment } from '../../environments/environment';
import { DesktopAppDownloadLinksComponent } from '../shared/desktop-app-download-links/desktop-app-download-links.component';
import {
  detectMobilePlatform,
  storeUrlForPlatform,
  tryOpenMobileAppWithStoreFallback,
} from '../shared/mobile-app-open.util';

const CLIENT_APP = {
  name: 'Anihis',
  scheme: 'com.anihis.app',
  androidPackage: 'com.anihis.app',
  appStoreUrl: AppStoreUrls.clientApp.ios,
  playStoreUrl: AppStoreUrls.clientApp.android,
};

type RedirectStatus = 'loading' | 'redirecting-store' | 'desktop';

@Component({
  selector: 'anihis-pet-gallery-share-open',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatProgressSpinnerModule,
    DesktopAppDownloadLinksComponent,
  ],
  templateUrl: './pet-gallery-share-open.component.html',
  styleUrl: './pet-gallery-share-open.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PetGalleryShareOpenComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);

  status = signal<RedirectStatus>('loading');
  postUid = signal('');
  private mobilePlatform = detectMobilePlatform();

  ngOnInit(): void {
    const uid = this.route.snapshot.paramMap.get('postUid')?.trim() ?? '';
    this.postUid.set(uid);
    if (!uid) {
      this.status.set('desktop');
      return;
    }
    this.handleRedirect(uid);
  }

  openStore(): void {
    window.location.href = this.getStoreUrl();
  }

  getStoreUrl(): string {
    return storeUrlForPlatform(this.mobilePlatform, CLIENT_APP);
  }

  getDesktopQrUrl(): string {
    const base = environment.portalUrl.replace(/\/$/, '');
    const uid = this.postUid();
    if (uid) {
      return `${base}/pet-gallery/share/${encodeURIComponent(uid)}`;
    }
    return `${base}/download/client`;
  }

  readonly clientAppStoreUrl = CLIENT_APP.appStoreUrl;
  readonly clientPlayStoreUrl = CLIENT_APP.playStoreUrl;

  private handleRedirect(postUid: string): void {
    const inAppPath = `pet-gallery/share/${encodeURIComponent(postUid)}`;
    const platform = tryOpenMobileAppWithStoreFallback({
      iosDeepLink: `${CLIENT_APP.scheme}://${inAppPath}`,
      androidIntentPath: inAppPath,
      androidPackage: CLIENT_APP.androidPackage,
      customScheme: CLIENT_APP.scheme,
      appStoreUrl: CLIENT_APP.appStoreUrl,
      playStoreUrl: CLIENT_APP.playStoreUrl,
      onStoreRedirect: () => this.status.set('redirecting-store'),
    });
    this.mobilePlatform = platform;

    if (platform === 'desktop') {
      this.status.set('desktop');
    }
  }
}
